import { useState, useCallback } from 'react'
import { logError } from 'utils/log'
import { getStorage, StorageType } from 'utils/storage'

export interface UseStorageProps<T> {
  type: StorageType
  key: string
  initialValue: T
  onError?: (type: 'get' | 'set', error: unknown) => void
}

export function useStorage<T>({
  type,
  key,
  initialValue,
  onError
}: UseStorageProps<T>): [T, (value: T) => void] {
  const [storageValue, setStorageValue] = useState(() => {
    const value = getStorage(type).getItem(key)
    if (!value) {
      return initialValue
    }

    try {
      return typeof initialValue === 'string'
        ? (value as unknown as T)
        : (JSON.parse(value) as T)
    } catch (error) {
      if (onError) {
        onError('get', error)
      }
      logError(error as string)

      return initialValue
    }
  })

  const setValue = useCallback(
    (value: T | undefined) => {
      try {
        const stateValue: T =
          value instanceof Function ? value(storageValue) : value

        setStorageValue(stateValue)
        getStorage(type).setItem(
          key,
          typeof stateValue === 'string'
            ? stateValue
            : JSON.stringify(stateValue)
        )
      } catch (error) {
        if (onError) {
          onError('set', error)
        }
      }
    },
    [key, onError, storageValue, type]
  )

  return [storageValue, setValue]
}
